import { isNil } from 'lodash';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, MenuItem, Popover } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import { SigFieldKeys, SigStatus } from 'enums/SigManagement.enum';
import { updateSigAssociationsFilter } from 'store/thunks/sigThunks';
import { validateMinLength } from 'common/forms/formValidations';

const dateToUTCString = (date) => {
  if (!date) return null;
  return date.toISOString();
};

const utcStringToLocalDate = (isoString) => {
  if (!isoString) return null;
  return new Date(isoString);
};

const SigsFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const sigFilter = useSelector(({ sigs: { filter } }) => ({
    ...filter,
    ...(filter?.createdAt && { createdAt: utcStringToLocalDate(filter.createdAt) }),
    ...(filter?.updatedAt && { updatedAt: utcStringToLocalDate(filter.updatedAt) }),
    status: isNil(filter?.status) ? SigStatus.ALL : filter?.status,
  }));

  const loading = useSelector(({ sigs }) => sigs.loading);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'SigFilterButton-Popover' : undefined;

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      updateSigAssociationsFilter({
        filter: {
          ...formData,
          createdAt: formData?.createdAt && dateToUTCString(formData.createdAt),
          updatedAt: formData?.updatedAt && dateToUTCString(formData.updatedAt),
          status: formData?.status === SigStatus.ALL ? undefined : formData?.status,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>
      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={sigFilter}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Sig'
                      name={SigFieldKeys.SIG}
                      id='SigFilterButton-sig-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Direction'
                      name={SigFieldKeys.DIRECTION}
                      id='SigFilterButton-direction-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Created At'
                      name={SigFieldKeys.CREATED_AT}
                      id='SigFilterButton-createdAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Updated At'
                      name={SigFieldKeys.UPDATED_AT}
                      id='SigFilterButton-updatedAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    {/* Filters the sig, not its directions -- a sig can be active
                        while some of its directions are not. */}
                    <SelectField
                      label='Sig Status'
                      name={SigFieldKeys.STATUS}
                      id='SigFilterButton-status-dropdown'
                    >
                      <MenuItem value={SigStatus.ALL}>All</MenuItem>
                      <MenuItem value={SigStatus.ACTIVE}>Active</MenuItem>
                      <MenuItem value={SigStatus.INACTIVE}>Inactive</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default SigsFilterButton;
